/**
 * ProjectBillingDetail — full drill-down of one workbook.
 *
 * Renders the exact stacked layout of the Excel:
 *   Project header → for each block: Scope table + Milestone schedule + Receipts ledger + Totals.
 *
 * Adds "AI · Why this matters" on any block with balance overdue, and lets the
 * user append a receipt (which optionally mirrors into the bank ledger).
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import WhyThisMatters from "../components/WhyThisMatters";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import {
    ArrowLeft, FolderKanban, Layers, Plus, Building2, User, Link as LinkIcon,
    Sparkles, CheckCircle2, X
} from "lucide-react";

export default function ProjectBillingDetail() {
    const { workbookId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [banks, setBanks] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const [r, b] = await Promise.all([
                api.get(`/project-billing/${workbookId}`),
                api.get("/data/bank_accounts"),
            ]);
            setData(r.data.workbook);
            setBanks(b.data.items || []);
        } catch (e) {
            toast.error("Failed to load workbook: " + (e.response?.data?.detail || e.message));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [workbookId]);

    if (loading) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-8">Loading workbook…</div>;
    if (!data) return (
        <div className="p-8 text-center">
            <div className="text-xs font-mono-fin uppercase tracking-wider text-[var(--neg)]">Workbook not found</div>
            <button onClick={() => navigate("/project-billing")} className="mt-4 text-xs font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline">Back to list</button>
        </div>
    );

    const s = data.summary || {};

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <button onClick={() => navigate("/project-billing")} data-testid="pbd-back" className="p-1.5 border border-panel hover:border-[var(--brand-amber)]/40 text-[var(--text-secondary)]"><ArrowLeft size={14} /></button>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)]">// PROJECT BILLING WORKBOOK · DETAIL</div>
            </div>

            <Panel testId="pbd-header" title={<span className="flex items-center gap-2"><FolderKanban className="text-[var(--brand-amber)]" size={20} /> {data.filename}</span>} subtitle={data.vendor_llp}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <KpiCard testId="pbd-projects" label="Projects" value={s.project_count} format="custom" accent />
                    <KpiCard testId="pbd-blocks" label="Blocks" value={s.block_count} format="custom" />
                    <KpiCard testId="pbd-agreement" label="Total Agreement" value={fmtINR(s.total_agreement_value)} format="custom" />
                    <KpiCard testId="pbd-received" label="Total Received" value={fmtINR(s.total_received)} format="custom" />
                    <KpiCard testId="pbd-balance" label="Total Balance" value={fmtINR(s.total_balance)} format="custom" />
                </div>

                {(data.linked_client_id || data.linked_project_id) && (
                    <div className="mt-3 flex gap-2 items-center flex-wrap">
                        {data.linked_client_id && (
                            <Link data-testid="pbd-link-client" to={`/accounts/client/${data.linked_client_id}`}
                                className="text-[10px] font-mono-fin uppercase tracking-wider border border-panel px-2 py-1 hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] flex items-center gap-1">
                                <User size={11} /> Client ledger: {data.linked_client_id} <LinkIcon size={10} />
                            </Link>
                        )}
                        {data.linked_project_id && (
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider border border-panel px-2 py-1 flex items-center gap-1">
                                <Building2 size={11} /> Linked project: {data.linked_project_id}
                            </div>
                        )}
                    </div>
                )}
            </Panel>

            {data.projects.map((p, pi) => (
                <ProjectBlock key={pi} project={p} projectIndex={pi} banks={banks} workbookId={workbookId} onRefresh={load} />
            ))}
        </div>
    );
}


function ProjectBlock({ project, projectIndex, banks, workbookId, onRefresh }) {
    return (
        <div className="border border-panel bg-[var(--bg-base)]/40 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-display font-bold text-[var(--text-primary)] flex items-center gap-2">
                    <FolderKanban className="text-[var(--brand-amber)]" size={16} />
                    {project.project_name}
                </div>
                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                    Agreement <span className="text-[var(--brand-amber)]">{fmtINR(project.totals?.agreement_value || 0)}</span>
                    · Received <span className="text-pos">{fmtINR(project.totals?.received || 0)}</span>
                    · Balance <span className="text-neg">{fmtINR(project.totals?.balance || 0)}</span>
                </div>
            </div>

            {project.blocks.map((b, bi) => (
                <BlockCard key={bi} block={b} projectIndex={projectIndex} blockIndex={bi}
                    projectName={project.project_name} banks={banks} workbookId={workbookId} onRefresh={onRefresh} />
            ))}
        </div>
    );
}


function BlockCard({ block, projectIndex, blockIndex, projectName, banks, workbookId, onRefresh }) {
    const [addOpen, setAddOpen] = useState(false);

    const agreement = block.totals?.agreement_value || 0;
    const received = block.totals?.received || 0;
    const balance = block.totals?.balance || 0;
    const ratio = agreement > 0 ? received / agreement : 1;    // guard divide-by-zero
    const isOverdue = balance > 0 && ratio < 0.9;
    const isFullyPaid = balance <= 0 && agreement > 0;

    return (
        <div className="border border-panel/60 bg-[var(--bg-base)]/60 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Layers size={14} className="text-[var(--brand-amber)]" />
                    <span className="font-mono-fin text-sm text-[var(--text-primary)]">Block {blockIndex + 1}: {block.block_name}</span>
                </div>
                <div className="flex items-center gap-2">
                    {isOverdue && (
                        <WhyThisMatters kind="cashflow"
                            title={`${projectName} · ${block.block_name} · ₹${block.totals.balance} balance pending`}
                            context={{ project: projectName, block: block.block_name,
                                agreement: block.totals?.agreement_value, received: block.totals?.received,
                                balance: block.totals?.balance }}
                            label="AI · Explain gap" compact />
                    )}
                    <button data-testid={`pbd-add-receipt-${projectIndex}-${blockIndex}`} onClick={() => setAddOpen(true)}
                        disabled={isFullyPaid}
                        title={isFullyPaid ? "Block is fully paid — no more receipts expected" : "Add a receipt to this block"}
                        className={`text-[10px] font-mono-fin uppercase tracking-wider border px-2 py-1 flex items-center gap-1 ${isFullyPaid
                            ? "border-panel text-[var(--text-muted)] cursor-not-allowed opacity-50"
                            : "border-[var(--pos)]/40 text-[var(--pos)] hover:bg-[var(--pos)] hover:text-black"}`}>
                        <Plus size={10} /> {isFullyPaid ? "Fully Paid" : "Add Receipt"}
                    </button>
                </div>
            </div>

            {/* SCOPES */}
            {block.scopes?.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Scope of Work</div>
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-1 pr-2">Type</th>
                                    <th className="text-right pr-2">Area</th>
                                    <th className="text-right pr-2">Rate</th>
                                    <th className="text-right pr-2">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {block.scopes.map((s, i) => (
                                    <tr key={i} className="border-b border-panel/40">
                                        <td className="py-1 pr-2 text-[var(--text-primary)] truncate max-w-md" title={s.title}>{s.title}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{s.area ? s.area.toLocaleString("en-IN") : "—"}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{s.rate ? s.rate.toLocaleString("en-IN") : "—"}</td>
                                        <td className="pr-2 text-right text-[var(--brand-amber)]">{s.amount ? fmtINR(s.amount) : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MILESTONES */}
            {block.milestones?.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Milestone Schedule</div>
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-1 pr-2 w-8">#</th>
                                    <th className="text-left pr-2">Stage</th>
                                    <th className="text-right pr-2">Demand %</th>
                                    <th className="text-right pr-2">Demand ₹</th>
                                    <th className="text-left pr-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {block.milestones.map((m, i) => (
                                    <tr key={i} className="border-b border-panel/40">
                                        <td className="py-1 pr-2 text-[var(--text-primary)]/70">{m.seq || i + 1}</td>
                                        <td className="pr-2 text-[var(--text-primary)] truncate max-w-md" title={m.stage}>{m.stage}</td>
                                        <td className="pr-2 text-right text-[var(--text-primary)]/85">{(m.demand_pct * 100).toFixed(1)}%</td>
                                        <td className="pr-2 text-right text-[var(--brand-amber)]">{fmtINR(m.demand_amount || 0)}</td>
                                        <td className="pr-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{m.status || "Pending"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* RECEIPTS */}
            {block.receipts?.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Receipts Ledger · {block.receipts.length} entries</div>
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-1 pr-2">Date</th>
                                    <th className="text-left pr-2">Type</th>
                                    <th className="text-right pr-2">Amount</th>
                                    <th className="pr-2">Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {block.receipts.map((r, i) => (
                                    <tr key={i} className="border-b border-panel/40">
                                        <td className="py-1 pr-2 text-[var(--text-primary)]/85 whitespace-nowrap">{fmtDate(r.date)}</td>
                                        <td className="pr-2 text-[var(--text-primary)]">{r.txn_type}</td>
                                        <td className="pr-2 text-right text-pos">{fmtINR(r.amount)}</td>
                                        <td className="pr-2 text-[var(--text-primary)]/70 text-[10px]">
                                            {r.bank_account_id && <span className="text-[var(--brand-amber)]">→ {r.bank_account_id}</span>}
                                            {r.note && <span className="ml-1">· {r.note}</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MATERIALS */}
            {block.materials?.length > 0 && (
                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Materials / Procurement</div>
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-1 pr-2">Item</th>
                                    <th className="text-right pr-2">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {block.materials.map((m, i) => (
                                    <tr key={i} className="border-b border-panel/40">
                                        <td className="py-1 pr-2 text-[var(--text-primary)]">{m.item}</td>
                                        <td className="pr-2 text-right text-[var(--brand-amber)]">{fmtINR(m.value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Totals footer */}
            <div className="border-t border-panel pt-2 flex items-center justify-between text-[10px] font-mono-fin uppercase tracking-wider">
                <span className="text-[var(--text-secondary)]">Agreement <span className="text-[var(--brand-amber)]">{fmtINR(block.totals?.agreement_value || 0)}</span></span>
                <span className="text-[var(--text-secondary)]">Received <span className="text-pos">{fmtINR(block.totals?.received || 0)}</span></span>
                <span className="text-[var(--text-secondary)]">Balance <span className="text-neg">{fmtINR(block.totals?.balance || 0)}</span></span>
            </div>

            {addOpen && (
                <AddReceiptModal onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); onRefresh(); }}
                    workbookId={workbookId} projectIndex={projectIndex} blockIndex={blockIndex}
                    blockName={block.block_name} banks={banks} />
            )}
        </div>
    );
}


function AddReceiptModal({ onClose, onDone, workbookId, projectIndex, blockIndex, blockName, banks }) {
    const [txnType, setTxnType] = useState("icici trf");
    const [amount, setAmount] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState("");
    const [bankId, setBankId] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const amt = Number(amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        if (!date) { toast.error("Enter a date"); return; }
        setBusy(true);
        try {
            await api.post(`/project-billing/${workbookId}/receipt`, {
                project_index: projectIndex,
                block_index: blockIndex,
                txn_type: txnType,
                amount: amt,
                date, note,
                bank_account_id: bankId || null,
            });
            toast.success("Receipt added" + (bankId ? " · mirrored to bank ledger" : ""));
            onDone();
        } catch (e) {
            toast.error("Failed: " + (e.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-3 flex items-center justify-between">
                    <div className="font-display font-bold text-[var(--text-primary)]">Add Receipt · Block {blockIndex + 1}: {blockName}</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Transaction Type</div>
                        <input value={txnType} onChange={(e) => setTxnType(e.target.value)} data-testid="pbd-rec-type"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Amount ₹</div>
                            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pbd-rec-amount"
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none" />
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Date</div>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="pbd-rec-date"
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none" />
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Post to Bank Ledger (optional)</div>
                        <select value={bankId} onChange={(e) => setBankId(e.target.value)} data-testid="pbd-rec-bank"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] focus:outline-none">
                            <option value="">— no bank link —</option>
                            {banks.map((b) => <option key={b.id} value={b.id}>{b.id} · {b.bank || b.name} · {b.acc_no || ""}</option>)}
                        </select>
                        <div className="text-[9px] font-mono-fin text-[var(--text-muted)] mt-1 italic">If selected, this receipt shows up in Bank Import → Stored Transactions and the account ledger drill-down.</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Note (optional)</div>
                        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="pbd-rec-note"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-body focus:border-[var(--brand-amber)] focus:outline-none" />
                    </div>
                    <button onClick={save} disabled={busy} data-testid="pbd-rec-save"
                        className="w-full bg-[var(--pos)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2">
                        <CheckCircle2 size={12} /> {busy ? "Saving…" : "Save Receipt"}
                    </button>
                </div>
            </div>
        </div>
    );
}
